import * as React from 'react';

import { type LayoutDimensions, useConfigurationTheme } from '@gitbook/react-uikit';

import {
    DesktopFrameDark,
    DesktopFrameLight,
    desktop,
    MobileFrameDark,
    MobileFrameLight,
    mobile,
    type PreviewDevice,
} from './devices';

/**
 * Render the frame of a device.
 */
export function PreviewDeviceFrame(props: {
    /**
     * Content with the iframe to render inside the device frame.
     */
    children: React.ReactNode;

    /**
     * Size of the device to emulate.
     */
    device?: 'mobile' | 'desktop';

    /**
     * Size of the frame to render in the DOM
     */
    frame: {
        maxWidth: number;
        maxHeight: number;
    };

    /**
     * Use a fixed height for the container to avoid layout shift.
     */
    fixedHeight?: boolean;

    /**
     * URL of the content to render in the frame.
     */
    src?: string;
}): React.ReactElement {
    const { device: deviceName = desktop, frame, fixedHeight = true, src, children } = props;
    const device = deviceName === 'desktop' ? desktop : mobile;

    const { width, height } = computeFrameSize(frame, device);

    const frameScaling = width / device.frameSize.width;

    const { color: currentTheme } = useConfigurationTheme();

    const DesktopFrame = currentTheme === 'dark' ? DesktopFrameDark : DesktopFrameLight;
    const MobileFrame = currentTheme === 'dark' ? MobileFrameDark : MobileFrameLight;
    const DeviceFrame = device.frameSource === 'desktop' ? DesktopFrame : MobileFrame;

    return (
        <div
            className="flex items-center"
            style={
                fixedHeight
                    ? {
                          // Use a fixed height, independant from the device to avoid layout shift for the mobile/desktop buttons
                          // when switching device
                          height: frame.maxHeight,
                      }
                    : undefined
            }
        >
            <div className="relative">
                <DeviceFrame width={width} height={height} url={src} />
                <div
                    className="absolute overflow-hidden"
                    style={{
                        top: Math.round(device.frameScreenPosition.y * frameScaling),
                        left: Math.round(device.frameScreenPosition.x * frameScaling),
                        width: Math.round(device.frameScreenSize.width * frameScaling),
                        height: Math.round(device.frameScreenSize.height * frameScaling),
                        borderBottomLeftRadius: Math.round(
                            device.frameScreenPosition.borderBottomLeftRadius * frameScaling
                        ),
                        borderBottomRightRadius: Math.round(
                            device.frameScreenPosition.borderBottomRightRadius * frameScaling
                        ),
                    }}
                >
                    <div
                        style={{
                            width: device.screenResolution.width,
                            height: device.screenResolution.height,
                            transform: `scale(${
                                (device.frameScreenSize.width * frameScaling) /
                                device.screenResolution.width
                            })`,
                            transformOrigin: 'top left',
                        }}
                    >
                        <React.Suspense fallback={<div className="shimmer" />}>
                            {children}
                        </React.Suspense>
                    </div>
                </div>
            </div>
        </div>
    );
}

function computeFrameSize(
    frame: {
        maxWidth: number;
        maxHeight: number;
    },
    device: PreviewDevice
): LayoutDimensions {
    const deviceRatio = device.frameSize.width / device.frameSize.height;

    let width = frame.maxWidth;
    let height = Math.round(width / deviceRatio);

    if (height > frame.maxHeight) {
        height = frame.maxHeight;
        width = Math.round(height * deviceRatio);
    }

    return { width, height };
}
